// Libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { isNotNil } from 'ramda';

// MUI
import { Container } from '@mui/material';
import Grid from '@mui/material/Grid';

// Store
import { useAppSelector } from '../../store/hooks';

// Core
import { paramRepository, ParameterValues, ResourceTextApplication } from '../../core/resources';
import log from '../../core/log';
import auth from '../../core/authorization';

// Common
import { ViewTitle } from '../../common/typography';
import ViewLoader from '../../common/ViewLoader';
import { contextControl } from '../../common/contextSelector/ContextSelector';
import contextUtils from '../../common/contextSelector/contextUtils';

// Interfaces
import defaultData from '../../core/defaultData';
import { ICompanyFarmId, IFarmDetails } from '../../interfaces/IBusinessEntities';
import { AliasTypeEnum, AnimalTypeEnum } from '../../interfaces/enums';
import { IFarmVisit, IFarmVisitor } from '../../interfaces/IFarmVisit';
import { GUIDType } from '../../interfaces/types';

// Api
import companyApi from '../../api/companyApi';
import { snellmanContactPersons } from '../../publicContactPersons/publicContactPersonsApi';
import FarmVisitAPI from '../../api/farmVisitApi';

// Feature
import BasicInfo from './BasicInfo';
import CoopInfo from './CoopInfo';
import PersonelInfo from './PersonelInfo';
import ProductionInfo from './ProductionInfo';
import MyFarmVisit from '../../farmVisit/MyFarmVisit';
import SlaughterVariablesView, {
    SlaughterVariablesViewProps,
} from '../../slaughterVariables/SlaughterVariablesView';
import { mapFarmVisit } from '../../farmVisit/helpers/data';
import { IFarmVisitNotificationLocationData } from '../../farmVisit/IFarmVisitNotificationLocationData';
import PreliminaryAnnouncementsSwitch from './PreliminaryAnnouncementsSwitch';

export interface FarmInfoSectionProps {
    bovineProduction: boolean;
    data: IFarmDetails;
    getParamText: (param: ParameterValues, code: number | string | null) => string;
    porkProduction: boolean;
}

export default function FarmInfo() {
    const loginStatus = useAppSelector((state) => state.loginStatus.data);
    const context = useAppSelector((state) => state.context);
    const location = useLocation();

    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaCompany', 'AnelmaLayout']);

    const hasParameters = () => location.state !== null;
    const [data, setData] = useState<IFarmDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(hasParameters());
    const [farmVisit, setFarmVisit] = useState<IFarmVisit>(defaultData.farmVisit());
    const [myFarmVisits, setMyFarmVisits] = useState<IFarmVisit[]>([]);
    const [visitors, setVisitors] = useState<IFarmVisitor[]>([]);
    const [farmVisitLocationData, setFarmVisitLocationData] =
        useState<IFarmVisitNotificationLocationData | null>(initializeFarmVisitLocationData());
    const [companyLocationData, setCompanyLocationData] = useState<ICompanyFarmId | null>(initializeCompanyLocationData());

    const sectionProps: FarmInfoSectionProps | null = data
        ? {
              bovineProduction:
                  !!data?.ProductionLines.find((i) => i.Type === AnimalTypeEnum.Bovine)
                      ?.FarmingTypes.length || false,
              data,
              getParamText,
              porkProduction:
                  !!data?.ProductionLines.find((i) => i.Type === AnimalTypeEnum.Pork)?.FarmingTypes
                      .length || false,
          }
        : null;

    const slaughterVariableViewProps: SlaughterVariablesViewProps | null = data
        ? {
              farmId: data?.Id ?? '',
              farmInfo: data,
              adminValues: false,
          }
        : null;

    // open from notifications
    useEffect(() => {
        setCompanyLocationData(initializeCompanyLocationData());
    }, [location.key]);

    useEffect(() => {
        if (context.data.currentContext) {
            initializeComponentData(context.data.currentContext.context);
        }
        else setData(null);
    }, [context]);

    useEffect(() => {
        if (isNotNil(farmVisitLocationData)) {
            handleContextChange(farmVisitLocationData.FarmId);
        }
    }, [farmVisitLocationData]);

    useEffect(() => {
        if (isNotNil(companyLocationData)) {
            handleContextChange(companyLocationData.FarmId);
        }
    }, [companyLocationData]);

    const initializeComponentData = (ctx: string) => {
        setLoading(true);

        companyApi.getCompanyByContext(ctx).then((response) => {
            // skip initial context load on having parameters
            if (hasParameters()) {
                if (response) {
                    let companyParameters = initializeCompanyLocationData();

                    if (companyParameters !== null && companyParameters.Id !== response.Entity.Id) {
                        return;
                    }

                    let farmVisitParameters = initializeFarmVisitLocationData();

                    if (farmVisitParameters !== null && farmVisitParameters.FarmId !== response.Entity.Id) {
                        return;
                    }
                }
            }

            const myFarmVisit: IFarmVisit = {
                ...farmVisit,
                FarmGUID: response?.Entity?.Id as GUIDType,
            };

            setFarmVisit(myFarmVisit);
            setData(response?.Entity as IFarmDetails);

            if (auth.canViewFarmVisitsProducer) {
                initialFarmVisitFetch(response?.Entity.Id ?? '');
            }

            Promise.all([
                fetchSnellmanContactPersons(),
                paramRepository.load(['AnelmaCompany', 'AnelmaGeneral']),
            ]).then(() => {
                location.state = null;
                setLoading(false);
            });
        });
    };

    const initialFarmVisitFetch = (farmId: string) => {
        return FarmVisitAPI.getFarmVisitsByFarmId(farmId).then((response) => {
            setMyFarmVisits(response.Items);
            setLoading(false);
        });
    };

    const fetchSnellmanContactPersons = () => {
        const visitorCollection: IFarmVisitor[] = [];
        return snellmanContactPersons(!!loginStatus.loggedIn).then((data) => {
            if (!data) {
                log('info', `'snellmanContactPersons' response returned no results.`);
                return;
            }
            data.Items.forEach((person) => {
                visitorCollection.push({
                    Id: person.PersonId,
                    FullName: person.Name,
                });
            });
            setVisitors(visitorCollection);
        });
    };

    /** Get company details and possibly change context selector's context when tilatunnus in the current selected context differs from farm visit notification's
     * @param locationData
     */
    const handleContextChange = (companyId: string) => {
        if (isNotNil(companyId)) {
            companyApi.getCompanyByGuid(companyId).then((response) => {
                if (!response) {
                    enqueueSnackbar(t('AnelmaSolmuErrors:10009'), {
                        variant: 'error',
                    });
                    return;
                }

                const producerNumber = response.Entity.Aliases.find(
                    (x) => x.Type === AliasTypeEnum.ProducerNumber
                )?.Value;

                if (isNotNil(producerNumber)) {
                    // if current context is the same, no need to switch
                    if (context.data.currentContext
                        && contextUtils.parseContextString(context.data.currentContext.context).producerNumber === producerNumber)
                        return;
                    else
                        contextControl.setFarm(producerNumber);
                }
            });
        }
    };

    function initializeFarmVisitLocationData() {
        return isNotNil(location.state) &&
            location.state?.options?.FarmVisitId &&
            location.state?.options?.FarmId
            ? ({
                FarmVisitId: location.state.options.FarmVisitId,
                FarmId: location.state.options.FarmId,
            } as IFarmVisitNotificationLocationData)
            : null;
    }

    function initializeCompanyLocationData() {
        return isNotNil(location.state) &&
            location.state?.options?.Id &&
            location.state?.options?.FarmId
            ? ({
                  Id: location.state.options.Id,
                  FarmId: location.state.options.FarmId,
              } as ICompanyFarmId)
            : null;
    }

    function getParamText(param: ParameterValues, code: number | string | null): string {
        if (!code) return '';
        return param.find((i) => i.code === `${code}`)?.text || `${code}`;
    }

    const mapFarmVisitToAllFarmVisitData = (farmVisit: IFarmVisit) => {
        const idx = myFarmVisits.findIndex((_) => _.Id === farmVisit.Id);

        if (idx !== -1) {
            const updatedState = [...myFarmVisits];
            if (farmVisit.Attachments && farmVisit.Attachments.length > 0)
                setMyFarmVisits(mapFarmVisit(farmVisit, updatedState, idx));
            else {
                farmVisit.CompanyName = updatedState[idx].CompanyName;
                farmVisit.ProducerNumber = updatedState[idx].ProducerNumber;
                updatedState[idx] = farmVisit;
                setMyFarmVisits(updatedState);
            }
        } else setMyFarmVisits((prev) => [...prev, farmVisit]);
    };

    const onClose = () => {
        setFarmVisitLocationData(null);
        setFarmVisit(defaultData.farmVisit());
    };

    return (
        <Container data-robot-id={'app-body-farminfo'}>
            <ViewTitle>{t('AnelmaLayout:1026')}</ViewTitle>

            {loading ? (
                <ViewLoader />
            ) : (
                <>
                    {sectionProps ? (
                        <>
                            <Grid container>
                                <Grid size={{ md: 6 }}>
                                    <BasicInfo {...sectionProps} />
                                </Grid>
                                <Grid size={{ md: 6 }}>
                                    <PersonelInfo {...sectionProps} />
                                    <br />
                                    <br />
                                    <ProductionInfo {...sectionProps} />
                                    <br />
                                    <br />
                                    <CoopInfo {...sectionProps} />
                                </Grid>
                            </Grid>
                            <br />
                            <br />
                            {auth.canViewFarmVisitsProducer && (
                                <Grid size={{ md: 12 }}>
                                    <MyFarmVisit
                                        showAddButton={true}
                                        farmDetails={data ? data : undefined} // At least temporary
                                        data={farmVisit}
                                        onClose={() => onClose()}
                                        columns={[]}
                                        visitors={visitors}
                                        handleMapFarmVisit={mapFarmVisitToAllFarmVisitData}
                                        deleteFarmVisitFromList={() => {}}
                                        myFarmVisits={myFarmVisits}
                                        loading={loading}
                                    />
                                </Grid>
                            )}
                            <br />
                            <br />
                { <PreliminaryAnnouncementsSwitch farmId={data?.Id} /> }
                            <Grid size={{ md: 12 }}>
                                {slaughterVariableViewProps && (
                                    <SlaughterVariablesView {...slaughterVariableViewProps} />
                                )}
                            </Grid>
                        </>
                    ) : (
                        t('AnelmaCompany:1124')
                    )}
                </>
            )}
        </Container>
    );
}
